import { useNavigate, useParams } from 'react-router';
import { ObservableLevelsEnum } from '../../../data/enums';
import { Button } from '../../../components/atoms/Button';
import { useEffect, useState } from 'react';
import { EquipmentModel } from '../../../data/types';
import DatabaseAssetsMethods from '../../../data/database.assets.methods';
import { inspectionDB } from '../../../data/database.structure';
import { ScreenHeading } from '../../../components/molecules/ScreenHeading';
import { ScreenSkeleton } from '../../../components/atoms/ScreenSkeleton';
import PhotoSelector from '../../../components/atoms/PhotoSelector';
import { ArrowLeft } from 'lucide-react';
import { RichTextField } from '../../../components/atoms/RichTextField';
import { ScreenObservationLayout } from '../../../components/templates/ScreenObservationLayout';
import { ObservableItemIdentifier } from '../../../api-contracts/other/ObservableItemIdentifier';
import { ErrorBanner } from '../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../hooks/useInspectionCached';
import { ObservationDraftScope } from '../../../components/ObservationDraftScope';

export const SideFrameScreen = () => {
  const { externalId, sideName } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const [equipment, setEquipment] = useState<EquipmentModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null); // reset error
    async function getEquipment() {
      try {
        const assetMethods = new DatabaseAssetsMethods(inspectionDB);

        const asset = inspection?.asset;

        if (!asset) {
          setError('Asset not found');
          return;
        }

        let equipments = asset.equipments;

        if (!equipments || equipments.length === 0) {
          equipments = await assetMethods.getAssetEquipment(asset.id);
        }

        const sideNameDefined = sideName
          ?.split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        const equipment = equipments.find((equipment) => {
          return equipment.name === sideNameDefined;
        });

        if (!equipment) {
          setError('Equipment not found');
          return;
        }

        setEquipment(equipment);
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    if (externalId && sideName && inspection) {
      getEquipment();
    }
  }, [externalId, sideName, inspection]);

  if (!equipment || !undercarriageCategory) {
    // Layout stability: same-shaped skeleton instead of a floating spinner over a
    // zero-height body (the arriving content then replaces reserved blocks).
    return <ScreenSkeleton fields={3} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;
  return (
    <ObservationDraftScope inspectionId={externalId ?? ''}>
      <div>
        <ScreenHeading
          eyebrow={'Photos'}
          title={'Side frame'}
          tag={sideName?.split('-')[0]}
          tooltipKey={'undercarriage-side-frame-screen-header'}
        />

        <ScreenObservationLayout className="pt-4">
          <PhotoSelector
            title="Photos"
            isMultiple={true}
            path={Undercarriage.SideFrame.Photo}
            dataLevel={ObservableLevelsEnum.equipment}
            entityId={equipment.id}
            entityExternalId={equipment.externalId}
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          />

          <RichTextField
            path={Undercarriage.SideFrame.Comments}
            label="Comments"
            dataLevel={ObservableLevelsEnum.equipment}
            entityId={equipment.id}
            entityExternalId={equipment.externalId}
            placeholder="Enter your comments here..."
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            className="pt-4"
          />

          <div className="flex items-center justify-center p-4">
            <Button
              variant="primary"
              prefixIcon={<ArrowLeft color="white" />}
              onClick={() => navigate(-1)}
            >
              Mark as complete
            </Button>
          </div>
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
